import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import { formatDate, formatMoney } from "@/utils/format";
import type { SchoolClass, Student } from "@/types";
import { useStudentDossier } from "@/hooks/useStudentDossier";
import { ENROLLMENT_STATUS_LABEL, ENROLLMENT_STATUS_TONE } from "@/utils/enrollment";
import { GUARDIAN_RELATION_LABEL } from "@/utils/enrollment";
import { getActiveSchoolYear } from "@/services/schoolService";
import StudentDocuments from "./StudentDocuments";

interface Props {
  open: boolean;
  onClose: () => void;
  student: Student | null;
  classes: SchoolClass[];
}

/** Fiche de l'élève : son dossier complet est chargé en un appel à l'ouverture. */
export default function StudentProfileModal({ open, onClose, student, classes }: Props) {
  const { dossier, error } = useStudentDossier(student?.id ?? null, open);
  if (!student) return null;

  const studentGuardians = dossier?.guardians ?? [];
  const history = dossier?.enrollments ?? [];

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? "—";
  const schoolYear = getActiveSchoolYear();
  const current =
    history.find(({ enrollment: e }) => e.schoolYear === schoolYear && e.status !== "CANCELLED")?.enrollment ?? null;

  return (
    <Modal open={open} onClose={onClose} title="Dossier élève" subtitle={student.matricule} icon="ri-folder-user-line" size="lg">
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <Avatar lastName={student.lastName} firstName={student.firstName} src={student.photo || undefined} size="lg" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold font-heading text-foreground-950">
              {student.firstName} {student.lastName}
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge tone="primary" icon="ri-hashtag">
                {student.matricule}
              </Badge>
              <Badge tone="secondary" icon="ri-genderless-line">
                {student.gender === "M" ? "Masculin" : "Féminin"}
              </Badge>
              <Badge tone="neutral" icon="ri-cake-2-line">
                {formatDate(student.birthDate)}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[
            { icon: "ri-map-pin-2-line", label: "Lieu de naissance", value: student.birthPlace || "—" },
            { icon: "ri-home-4-line", label: "Adresse", value: student.address || "—" },
            { icon: "ri-phone-line", label: "Téléphone", value: student.phone || "—" },
            { icon: "ri-sticky-note-line", label: "Remarques", value: student.notes || "—" },
          ].map((row) => (
            <div key={row.label} className="rounded-md border border-background-200 bg-background-100/50 px-3.5 py-2.5">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-foreground-400">
                <i className={row.icon}></i>
                {row.label}
              </p>
              <p className="mt-0.5 text-sm text-foreground-800">{row.value}</p>
            </div>
          ))}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Responsables ({studentGuardians.length})
          </p>
          <div className="space-y-2">
            {studentGuardians.map(({ link, guardian }) => (
              <div key={guardian.id} className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-3.5 py-2.5">
                <div className="flex items-center gap-3">
                  <Avatar lastName={guardian.lastName} firstName={guardian.firstName} size="sm" />
                  <div>
                    <p className="text-sm font-medium text-foreground-900">
                      {guardian.firstName} {guardian.lastName}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {guardian.profession || "—"} • {guardian.phone}
                    </p>
                  </div>
                </div>
                <Badge tone={link.isPrimary ? "primary" : "neutral"}>
                  {link.isPrimary ? "Principal" : GUARDIAN_RELATION_LABEL[link.relation]}
                </Badge>
              </div>
            ))}
            {studentGuardians.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-400">
                {dossier ? "Aucun responsable enregistré." : error ?? "Chargement du dossier..."}
              </p>
            )}
          </div>
        </div>

        {dossier && (
          <StudentDocuments student={student} enrollment={current} className={current ? className(current.classId) : ""} />
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Historique des inscriptions
          </p>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100">
                <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-3.5 py-2 font-medium">Année</th>
                  <th className="px-3.5 py-2 font-medium">Classe</th>
                  <th className="px-3.5 py-2 font-medium">Date</th>
                  <th className="px-3.5 py-2 font-medium">Statut</th>
                  <th className="px-3.5 py-2 font-medium text-right">Payé</th>
                  <th className="px-3.5 py-2 font-medium text-right">Reste</th>
                </tr>
              </thead>
              <tbody>
                {history.map(({ enrollment: e, balance }) => (
                  <tr key={e.id} className="border-t border-background-200">
                    <td className="px-3.5 py-2.5 font-medium text-foreground-900">{e.schoolYear}</td>
                    <td className="px-3.5 py-2.5 text-foreground-700">{className(e.classId)}</td>
                    <td className="px-3.5 py-2.5 text-foreground-500">{formatDate(e.enrollmentDate)}</td>
                    <td className="px-3.5 py-2.5">
                      <Badge tone={ENROLLMENT_STATUS_TONE[e.status]}>{ENROLLMENT_STATUS_LABEL[e.status]}</Badge>
                    </td>
                    <td className="px-3.5 py-2.5 text-right text-primary-700">{formatMoney(balance.totalPaid)}</td>
                    <td className="px-3.5 py-2.5 text-right text-accent-700">
                      {e.status === "CANCELLED" ? "—" : formatMoney(balance.remaining)}
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3.5 py-4 text-center text-xs text-foreground-400">
                      {dossier ? "Aucune inscription." : error ?? "Chargement du dossier..."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}